import { Controller } from "../vendor/stimulus.js";

const IOS_NUDGE_DELAY = 100;

export default class extends Controller {
  static targets = [
    "textarea",
    "base64ImageData",
    "errorMessage",
  ];

  static values = {
    collapseLayout: { type: Boolean, value: false}
  }

  connect() {
    this.isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);

    this.loaderTimeout = Math.floor(Math.random() * 2001) + 1000;
    this.conversationContainer = this.getConversationContainer();

    this.loaderTemplate = `
      <div class="dot-loader" aria-label="Einen Moment, bitte…"></div>
    `;
  }

  disconnect() {
    if (this.loaderTimeoutId) {
      window.clearTimeout(this.loaderTimeoutId);
      this.loaderTimeoutId = null;
    }
    if (this.nudgeTimeoutId) {
      clearTimeout(this.nudgeTimeoutId);
      this.nudgeTimeoutId = null;
    }
  }

  submit(event) {
    event?.preventDefault();
    if (this.isSubmitting) return;

    // Apply skeleton loading effect to moodboard immediately
    this.applyMoodboardLoadingState();

    const proceed = () => {
      this.hideError();

      const formData = new FormData(this.element);

      // Include msg_id for "restart" behavior (like option selection)
      // Find the last message ID in the conversation
      const conversationContainer = document.getElementById('conversation');
      const lastMessage = conversationContainer?.querySelector('[id^="msg-"]:last-of-type');
      const lastMsgId = lastMessage ? lastMessage.id.replace('msg-', '') : '0';
      formData.append('msg_id', lastMsgId);

      if(this.collapseLayoutValue == true) {
        const collapseDelay = this.isIos ? (400 + IOS_NUDGE_DELAY) : 400;
        setTimeout(() => {
          const mainElement = document.querySelector("main");
          mainElement.classList.add("collapse-intro-sections")
        }, collapseDelay);
      }

      this.setSubmitting(true);
      this.disableForm();
      const action = this.element.getAttribute("action") || this.element.action || window.location.href;
      const method = "POST";

      this.sendRequest(action, method, formData)
        .then((html) => this.handleResponse(html))
        .catch((error) => {
          console.error("FormOutletController submit failed", error);
          this.handleRequestError();
          this.enableForm();
          this.setSubmitting(false);
        });
    };

    if (this.isIos) {
      if (this.nudgeTimeoutId) {
        clearTimeout(this.nudgeTimeoutId);
      }
      this.nudgeTimeoutId = setTimeout(() => {
        this.nudgeViewportAfterKeyboard();
        this.nudgeTimeoutId = null;
        proceed();
      }, IOS_NUDGE_DELAY);
      return;
    }

    proceed();
  }

  sendRequest(action, method, formData) {
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    const options = { method, headers, body: formData, credentials: 'include' };
    return fetch(action, options).then((response) => {
      if (!response.ok && response.status !== 202) {
        this.emitGlobalXhrFailed(response.status);
        throw new Error(`Request failed with status ${response.status}`);
      }
      // 202 Accepted = success, SSE will handle UI updates
      return response.status === 202 ? null : response.text();
    }).catch((error) => {
      this.emitGlobalXhrFailed();
      throw error;
    });
  }

  handleResponse(html) {
    // If no HTML (202 response), SSE handles UI - just clear and enable form
    if (!html) {
      this.clearTextarea();
      this.enableForm();
      this.setSubmitting(false);
      return;
    }

    const replaceContent = () => {
      const targetContainer = this.getConversationContainer();
      if (!targetContainer) {
        this.cleanLoader();
        return;
      }
      this.revealConversationContainer(targetContainer);

      const template = document.createElement("template");
      template.innerHTML = html;
      const fragment = template.content;

      if (fragment.childNodes.length === 0 && this.loaderNode) {
        this.loaderNode.remove();
        this.loaderNode = null;
        return;
      }

      const loaderNode = this.loaderNode;
      if (loaderNode) {
        loaderNode.remove();
        this.loaderNode = null;
      }

      targetContainer.appendChild(fragment);
    };

    if (this.loaderTimeout > 0) {
      this.loaderTimeoutId = window.setTimeout(() => {
        this.loaderTimeoutId = null;
        replaceContent();
      }, this.loaderTimeout);
    } else {
      replaceContent();
    }
  }

  clearTextarea() {
    if (this.hasTextareaTarget) {
      this.textareaTarget.value = '';
      this.textareaTarget.dispatchEvent(new Event('input'));
    }
  }

  handleError() {
    this.cleanLoader();
  }

  handleRequestError() {
    this.handleError();
    this.showError();
  }

  disableForm() {
    this.element.classList.add("disable-form");
  }

  enableForm() {
    this.element.classList.remove("disable-form");
  }

  setSubmitting(state) {
    this.isSubmitting = state;
    if (state) {
      this.disabledButtons = Array.from(this.element.querySelectorAll("button"));
      this.disabledInputs = Array.from(this.element.querySelectorAll("input, textarea"));
      this.disabledButtons.forEach((button) => {
        button.disabled = true;
      });
      this.disabledInputs.forEach((input) => {
        input.disabled = true;
      });
      return;
    }

    if (this.disabledButtons) {
      this.disabledButtons.forEach((button) => {
        button.disabled = false;
      });
      this.disabledButtons = null;
    }
    if (this.disabledInputs) {
      this.disabledInputs.forEach((input) => {
        input.disabled = false;
      });
      this.disabledInputs = null;
    }
  }

  showError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("show-error");
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("show-error");
    }
  }

  findConversationContainer() {
    const section = this.element.closest("section");
    if (!section) return null;
    const candidate = section.nextElementSibling;
    if (candidate && candidate.classList?.contains("conversation-container")) {
      return candidate;
    }
    return null;
  }

  getConversationContainer() {
    if (this.conversationContainer && document.body.contains(this.conversationContainer)) {
      return this.conversationContainer;
    }
    this.conversationContainer = this.findConversationContainer();
    if (this.conversationContainer) {
      this.revealConversationContainer(this.conversationContainer);
    }
    return this.conversationContainer;
  }

  cleanLoader() {
    if (this.loaderNode) {
      this.loaderNode.remove();
      this.loaderNode = null;
    }
  }

  revealConversationContainer(container) {
    if (!container) return;
    container.hidden = false;
    container.classList?.remove("hidden");
    container.removeAttribute("hidden");
  }

  emitGlobalXhrFailed(status = null) {
    window.dispatchEvent(new CustomEvent("xhr-failed", { detail: { status } }));
  }

  nudgeViewportAfterKeyboard() {
    const currentY = window.scrollY || 0;
    window.scrollTo(0, currentY + 1);
    window.requestAnimationFrame(() => {
      window.scrollTo(0, currentY);
      window.requestAnimationFrame(() => window.scrollTo(0, currentY));
    });
  }

  /**
   * Apply skeleton loading effect to moodboard immediately on submit.
   * This provides instant visual feedback while waiting for SSE response.
   */
  applyMoodboardLoadingState() {
    const moodboard = document.querySelector('.moodboard');
    if (!moodboard) return;

    // ONLY add shimmer overlay - keep images visible underneath
    moodboard.classList.add('is-loading');
  }
}
